import { useMedplum } from '@medplum/react'
import type { Encounter } from '@medplum/fhirtypes'
import { useQuery } from '@tanstack/react-query'

function providerFromEncounter(encounter: Encounter | undefined): string | undefined {
  const reference = encounter?.participant?.find((p) =>
    p.individual?.reference?.startsWith('Practitioner/'),
  )?.individual?.reference
  return reference?.split('/')[1]
}

/**
 * The practitioner a note's charges are quoted against (PD-1647): whoever
 * rendered the service, never whoever has the editor open. An assisting nurse
 * opening a physician's note must not re-tier that physician's line, and an
 * RN's own line must be refused even when a physician is looking at it.
 * `Encounter.participant[].individual` is the treating clinician — `visit.service.ts`
 * stamps it at creation; the locked viewer reads `DocumentReference.authenticator`
 * for the same purpose.
 *
 * While an encounter exists but has not loaded yet, this returns `undefined`
 * rather than the fallback: an undefined practitioner disables the quote, whereas
 * quoting the caller for even one autosave can refuse — and drop — a line that
 * belongs to the rendering provider. The fallback is the answer when there is no
 * encounter to ask, or when the encounter has loaded without a Practitioner
 * participant (legacy or imported encounters; app-created ones always carry one).
 * A failed read is not an answer — `isSuccess` gates the fallback, not `isFetched`
 * (which is also true after an error), so a permission or network failure keeps
 * the quote disabled instead of pricing the signer.
 */
export function useRenderingProviderId(input: {
  encounter?: Encounter
  encounterId?: string
  fallbackId?: string
}): string | undefined {
  const medplum = useMedplum()
  const fromLoaded = providerFromEncounter(input.encounter)
  // Create mode (call page, chart side panel) never loads the Encounter into
  // state, so the id is all we have.
  const encounterId = input.encounterId
  const { data, isSuccess } = useQuery<Encounter>({
    queryKey: ['rendering-provider-encounter', { encounterId: encounterId ?? 'none' }],
    enabled: !fromLoaded && Boolean(encounterId),
    staleTime: 1000 * 60 * 60,
    retry: false,
    queryFn: async () => medplum.readResource('Encounter', encounterId!),
  })
  if (fromLoaded) return fromLoaded
  if (encounterId) return providerFromEncounter(data) ?? (isSuccess ? input.fallbackId : undefined)
  return input.fallbackId
}
